import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { api } from "@/lib/api";
import { useFilters } from "@/hooks/useFilters";
import { useUrlState } from "@/hooks/useUrlState";
import { pivotTo } from "@/lib/pivot";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { Card, CardHeader, EmptyState, ErrorState, Skeleton, Badge } from "@/components/primitives";
import { fmtLatency, fmtNumber, fmtPct, fmtSignedPct, truncateMiddle } from "@/lib/format";
import { countryName } from "@/config/geo";
import { HIGH_RISK_THRESHOLD, SUSPICIOUS_THRESHOLD } from "@/config/riskBands";
import type { CampaignV1, RepeatOffenderRow } from "@/types/v1";

type Tone = "neutral" | "watch" | "suspicious" | "high" | "good";

interface BriefItem {
  id: string;
  title: string;
  value: string;
  tone: Tone;
  context: string;
  data: string;
  calculation: string;
  actionLabel?: string;
  onClick?: () => void;
}

const P95_LATENCY_WARN_MS = 100;
const ERROR_WARN_RATE = 0.005;

export function PriorityBrief() {
  const { filters } = useFilters();
  const { tick } = useAutoRefresh();
  const [, patchUrl] = useUrlState();

  const summary = useQuery({
    queryKey: ["summary", filters, tick],
    queryFn: ({ signal }) => api.summary(filters, signal),
  });
  const targeted = useQuery({
    queryKey: ["targeted-accounts", filters, tick],
    queryFn: ({ signal }) => api.targetedAccounts(filters, signal),
  });
  const tooling = useQuery({
    queryKey: ["top-ja4-by-ips", filters, tick],
    queryFn: ({ signal }) => api.topJa4ByIps(filters, signal),
  });
  const repeat = useQuery({
    queryKey: ["repeat-offenders", filters, tick],
    queryFn: ({ signal }) => api.repeatOffenders(filters, signal),
  });
  const countries = useQuery({
    queryKey: ["globalActivity", filters, "highRisk", tick],
    queryFn: ({ signal }) => api.globalActivity(filters, "highRisk", signal),
  });
  const campaigns = useQuery({
    queryKey: ["campaigns", filters, tick],
    queryFn: ({ signal }) => api.campaigns(filters, signal),
  });
  const health = useQuery({
    queryKey: ["doHealth", filters, tick],
    queryFn: ({ signal }) => api.doHealth(filters, signal),
  });

  const items = useMemo(() => {
    if (!summary.data?.data) return [];
    return buildItems({
      summary: summary.data.data,
      targeted: targeted.data?.data,
      tooling: tooling.data?.data ?? [],
      repeat: repeat.data?.data,
      countries: countries.data?.data ?? [],
      campaigns: campaigns.data?.data ?? [],
      health: health.data?.data,
      patchUrl,
    });
  }, [summary.data, targeted.data, tooling.data, repeat.data, countries.data, campaigns.data, health.data, patchUrl]);

  const optionalErrors = [targeted.error, tooling.error, repeat.error, countries.error, campaigns.error, health.error].filter(Boolean).length;

  return (
    <Card>
      <CardHeader
        title="Top 10 things to know"
        subtitle="Prioritised signals from the active window. Click an item to open the matching investigation view when a real filter exists."
        action={optionalErrors ? <Badge tone="watch">partial data</Badge> : undefined}
      />
      {summary.isLoading ? (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          {Array.from({ length: 10 }).map((_, i) => <Skeleton key={i} className="h-44" />)}
        </div>
      ) : summary.error ? (
        <ErrorState error={summary.error} onRetry={() => summary.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState title="No overview signals in this window." />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          {items.map((item, index) => <BriefTile key={item.id} item={item} index={index} />)}
        </div>
      )}
    </Card>
  );
}

function BriefTile({ item, index }: { item: BriefItem; index: number }) {
  const content = (
    <>
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] font-medium text-fg-subtle">#{index + 1}</div>
        <Badge tone={item.tone}>{item.tone === "neutral" ? "signal" : item.tone}</Badge>
      </div>
      <div className="mt-2 text-sm font-semibold text-fg">{item.title}</div>
      <div className="mt-1 font-mono text-2xl font-semibold text-fg">{item.value}</div>
      <p className="mt-2 text-xs text-fg-muted">{item.context}</p>
      <div className="mt-3 space-y-1 border-t border-border pt-2 text-[11px] leading-5 text-fg-subtle">
        <div><span className="font-medium text-fg-muted">Data:</span> {item.data}</div>
        <div><span className="font-medium text-fg-muted">Calc:</span> {item.calculation}</div>
      </div>
      {item.actionLabel && <div className="mt-2 text-[11px] font-medium text-accent">{item.actionLabel}</div>}
    </>
  );

  if (!item.onClick) {
    return <div className="rounded-lg border border-border bg-surface-2 p-3">{content}</div>;
  }

  return (
    <button
      type="button"
      onClick={item.onClick}
      className={clsx(
        "rounded-lg border border-border bg-surface-2 p-3 text-left transition-colors",
        "hover:border-accent/60 hover:bg-surface-3 focus:outline-none focus:ring-2 focus:ring-accent/40",
      )}
    >
      {content}
    </button>
  );
}

function buildItems({
  summary,
  targeted,
  tooling,
  repeat,
  countries,
  campaigns,
  health,
  patchUrl,
}: {
  summary: import("@/types/v1").SummaryV1;
  targeted?: import("@/types/v1").TargetedAccountsV1;
  tooling: import("@/types/v1").TopJa4ByIpsV1Row[];
  repeat?: import("@/types/v1").RepeatOffendersV1;
  countries: import("@/types/v1").CountryActivityV1[];
  campaigns: CampaignV1[];
  health?: import("@/types/v1").DoHealthV1;
  patchUrl: ReturnType<typeof useUrlState>[1];
}): BriefItem[] {
  const highShare = summary.totalEvents ? summary.highRisk / summary.totalEvents : 0;
  const suspiciousShare = summary.totalEvents ? summary.suspicious / summary.totalEvents : 0;
  const topEmail = targeted?.emails[0];
  const topBusiness = targeted?.businesses[0];
  const topTooling = tooling[0];
  const topRepeat = bestRepeat(repeat);
  const topCountry = countries[0];
  const topCampaign = campaigns[0];
  const healthWarning = bestHealthWarning(health);

  return [
    {
      id: "high-risk-now",
      title: "Clearly suspicious requests",
      value: fmtNumber(summary.highRisk, { compact: true }),
      tone: summary.highRisk > 0 ? "high" : "good",
      context: `${fmtPct(highShare * 100, 1)} of scored requests are at or above ${HIGH_RISK_THRESHOLD}. ${deltaText(summary.delta?.highRisk)}`,
      data: "Summary high-risk count for the active time window.",
      calculation: `Weighted count of events with risk score >= ${HIGH_RISK_THRESHOLD}.`,
      actionLabel: "Open high-risk events",
      onClick: () => pivotTo(patchUrl, { riskBands: "high" }, "investigations"),
    },
    {
      id: "suspicious-now",
      title: "Borderline suspicious requests",
      value: fmtNumber(summary.suspicious, { compact: true }),
      tone: summary.suspicious > 0 ? "suspicious" : "good",
      context: `${fmtPct(suspiciousShare * 100, 1)} of scored requests are below high risk but still worth watching.`,
      data: "Summary suspicious count for the active time window.",
      calculation: `Weighted count of events with score ${SUSPICIOUS_THRESHOLD}-${HIGH_RISK_THRESHOLD - 1}.`,
      actionLabel: "Open suspicious events",
      onClick: () => pivotTo(patchUrl, { riskBands: "suspicious" }, "investigations"),
    },
    {
      id: "worst-tail-risk",
      title: "Worst-tail risk score",
      value: String(Math.round(summary.p95RiskScore)),
      tone: summary.p95RiskScore >= HIGH_RISK_THRESHOLD ? "high" : summary.p95RiskScore >= SUSPICIOUS_THRESHOLD ? "suspicious" : "watch",
      context: "Shows how severe the highest-scoring 5% of traffic is, without letting one outlier define the view.",
      data: "Risk scores from all matching events in the active window.",
      calculation: "Weighted 95th percentile: 95% of events scored at or below this value.",
      actionLabel: "Open high-risk events",
      onClick: () => pivotTo(patchUrl, { riskBands: "high" }, "investigations"),
    },
    topEmail ? {
      id: "top-email",
      title: "Most-targeted account",
      value: truncateMiddle(topEmail.value, 22),
      tone: "high",
      context: `${fmtNumber(topEmail.highRisk, { compact: true })} high-risk events hit this email out of ${fmtNumber(topEmail.events, { compact: true })} total events.`,
      data: "Email addresses attached to matching events.",
      calculation: "Groups events by email and orders by high-risk event count.",
      actionLabel: "Investigate this email",
      onClick: () => pivotTo(patchUrl, { email: topEmail.value }, "investigations"),
    } : emptyItem("top-email", "Most-targeted account", "No email with high-risk traffic", "targeted account data"),
    topBusiness ? {
      id: "top-business",
      title: "Most-targeted business",
      value: truncateMiddle(topBusiness.value, 22),
      tone: "high",
      context: `${fmtNumber(topBusiness.highRisk, { compact: true })} high-risk events hit this business out of ${fmtNumber(topBusiness.events, { compact: true })} total events.`,
      data: "Business IDs attached to matching events.",
      calculation: "Groups events by business ID and orders by high-risk event count.",
      actionLabel: "Investigate this business",
      onClick: () => pivotTo(patchUrl, { businessId: topBusiness.value }, "investigations"),
    } : emptyItem("top-business", "Most-targeted business", "No business with high-risk traffic", "targeted business data"),
    topTooling ? {
      id: "top-tooling",
      title: "Same tooling across many sources",
      value: `${fmtNumber(topTooling.distinctIps)} IPs`,
      tone: topTooling.highRisk > 0 ? "suspicious" : "watch",
      context: `${truncateMiddle(topTooling.ja4, 22)} appeared across ${fmtNumber(topTooling.events, { compact: true })} events, including ${fmtNumber(topTooling.highRisk, { compact: true })} high-risk events.`,
      data: "JA4 TLS fingerprints and source IPs from matching events.",
      calculation: "Groups by JA4, counts distinct IPs, then orders by widest IP spread.",
      actionLabel: "Investigate this JA4",
      onClick: () => pivotTo(patchUrl, { ja4: topTooling.ja4 }, "investigations"),
    } : emptyItem("top-tooling", "Same tooling across many sources", "No JA4 spans multiple IPs", "JA4 and source IP data"),
    topRepeat ? {
      id: "repeat-offender",
      title: "Repeat offender",
      value: topRepeat.label,
      tone: "suspicious",
      context: `${fmtNumber(topRepeat.row.yesterday, { compact: true })} high-risk events yesterday and ${fmtNumber(topRepeat.row.today, { compact: true })} today.`,
      data: "High-risk IP, JA4, and ASN rows from today and yesterday.",
      calculation: "Keeps only entities present in both days, then orders by combined high-risk count.",
      actionLabel: `Investigate this ${topRepeat.kind.toUpperCase()}`,
      onClick: () => pivotTo(patchUrl, { [topRepeat.filterKey]: topRepeat.row.value }, "investigations"),
    } : emptyItem("repeat-offender", "Repeat offender", "No entity is high-risk on both days", "today/yesterday high-risk intersections"),
    topCountry ? {
      id: "top-country",
      title: "Top high-risk country",
      value: `${topCountry.country} ${fmtNumber(topCountry.highRisk, { compact: true })}`,
      tone: topCountry.highRisk > 0 ? "suspicious" : "good",
      context: `${countryName(topCountry.country)} has the highest high-risk count in the active window.`,
      data: "Country code attached to matching events.",
      calculation: "Groups by country and orders by weighted high-risk event count.",
      actionLabel: "Investigate this country",
      onClick: () => pivotTo(patchUrl, { country: topCountry.country }, "investigations"),
    } : emptyItem("top-country", "Top high-risk country", "No country high-risk data", "country roll-up data"),
    topCampaign ? campaignItem(topCampaign, patchUrl) : emptyItem("top-campaign", "Campaign needing review", "No active campaign meets the current threshold", "campaign clustering data"),
    healthWarning ? {
      id: "scorer-health",
      title: "Scorer health warning",
      value: healthWarning.value,
      tone: "watch",
      context: `${healthWarning.doClass} is outside the Overview health threshold.`,
      data: "Durable Object class latency and error measurements.",
      calculation: `Flags p95 latency above ${P95_LATENCY_WARN_MS} ms or error rate above ${fmtPct(ERROR_WARN_RATE * 100, 2)}.`,
      actionLabel: "Open Scorer Health",
      onClick: () => patchUrl({ tab: "scorerHealth" }),
    } : {
      id: "scorer-health",
      title: "Scorer health",
      value: fmtLatency(summary.p95ScoringLatencyMs),
      tone: summary.p95ScoringLatencyMs > P95_LATENCY_WARN_MS ? "watch" : "good",
      context: "Scoring latency is shown so slow scoring does not get mistaken for attack activity.",
      data: "Summary p95 scoring latency for matching events.",
      calculation: "Weighted 95th percentile of scorer latency in milliseconds.",
      actionLabel: "Open Scorer Health",
      onClick: () => patchUrl({ tab: "scorerHealth" }),
    },
  ];
}

function emptyItem(id: string, title: string, value: string, data: string): BriefItem {
  return {
    id,
    title,
    value,
    tone: "neutral",
    context: "No matching signal was returned for the active window.",
    data: `Looks at ${data} after the current filters are applied.`,
    calculation: "Returns empty when no row meets that card's threshold.",
  };
}

function campaignItem(campaign: CampaignV1, patchUrl: ReturnType<typeof useUrlState>[1]): BriefItem {
  return {
    id: "top-campaign",
    title: "Campaign needing review",
    value: truncateMiddle(campaign.title, 28),
    tone: campaign.severity,
    context: `${fmtNumber(campaign.events, { compact: true })} events, ${fmtNumber(campaign.highRisk, { compact: true })} high-risk, p95 score ${Math.round(campaign.p95Score)}.`,
    data: "Correlated endpoint, ASN, and JA4 clusters from matching events.",
    calculation: "Uses the campaign endpoint's grouping and severity rules, ordered by high-risk count then volume.",
    actionLabel: "Open campaign events",
    onClick: () => pivotTo(patchUrl, campaign.filterPreset, "investigations"),
  };
}

function bestRepeat(repeat?: import("@/types/v1").RepeatOffendersV1): {
  kind: "ip" | "ja4" | "asn";
  filterKey: "ip" | "ja4" | "asn";
  label: string;
  row: RepeatOffenderRow;
} | null {
  const candidates = [
    ...(repeat?.ips ?? []).map((row) => ({ kind: "ip" as const, filterKey: "ip" as const, label: row.value, row })),
    ...(repeat?.ja4s ?? []).map((row) => ({ kind: "ja4" as const, filterKey: "ja4" as const, label: truncateMiddle(row.value, 18), row })),
    ...(repeat?.asns ?? []).map((row) => ({ kind: "asn" as const, filterKey: "asn" as const, label: `AS${row.value}`, row })),
  ];
  return candidates.sort((a, b) => b.row.combined - a.row.combined)[0] ?? null;
}

function bestHealthWarning(health?: import("@/types/v1").DoHealthV1): { doClass: string; value: string } | null {
  const warnings = (health?.perClass ?? [])
    .filter((row) => row.p95Ms > P95_LATENCY_WARN_MS || row.errorRate > ERROR_WARN_RATE)
    .sort((a, b) => Math.max(b.p95Ms / P95_LATENCY_WARN_MS, b.errorRate / ERROR_WARN_RATE) - Math.max(a.p95Ms / P95_LATENCY_WARN_MS, a.errorRate / ERROR_WARN_RATE));
  const top = warnings[0];
  if (!top) return null;
  return {
    doClass: top.doClass,
    value: top.p95Ms > P95_LATENCY_WARN_MS ? fmtLatency(top.p95Ms) : `${fmtPct(top.errorRate * 100, 2)} errors`,
  };
}

function deltaText(delta: number | null | undefined): string {
  if (delta == null) return "No previous-window comparison is available.";
  if (delta === 0) return "No change from the previous equal-length window.";
  return `${fmtSignedPct(delta * 100, 1)} vs the previous equal-length window.`;
}
